// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect }      from 'react'
import { useOnboardingStore, useAuthStore, useWizardStore } from '@/store'
import { createProfile }      from '@/services/profile'
import { saveModuleProgress } from '@/services/profile'
import { useToast }           from '@/hooks/useToast'
import { HOME_ROUTE } from '@/lib/constants/routes'
import { DEMO_MODE, DEMO_PASSWORD } from '@/config/demo'
import { AlertBanner }        from '@/components/feedback'
import { OnboardingProgress } from './OnboardingProgress'
import { OnboardingNav }      from './OnboardingNav'
import { Step1Welcome }       from './steps/Step1Welcome'
import { Step2Name }          from './steps/Step2Name'
import { Step3DateOfBirth }   from './steps/Step3DateOfBirth'
import { Step4Contact }       from './steps/Step4Contact'
import { Step5Location }      from './steps/Step5Location'
import { Step6MaritalStatus } from './steps/Step6MaritalStatus'
import { Step7Children }      from './steps/Step7Children'
import { Step8Password }      from './steps/Step8Password'
import { Step9Privacy }       from './steps/Step9Privacy'
import type { OnboardingData } from '@/types'
import { isValidEmail, isValidDutchPostalCode } from '@/lib/utils'

// ── Staplabels - privacy nu op positie 2, vóór persoonsgegevens ───────────────
const STEP_LABELS = [
  'Welkom',        // 1
  'Privacy',       // 2  ← verplaatst van 9 naar 2
  'Naam',          // 3
  'Geboortedatum', // 4
  'Contactgegevens', // 5
  'Adres',         // 6
  'Burgerlijke staat', // 7
  'Kinderen',      // 8
  'Wachtwoord',    // 9
]

// ── Validatie per stap ─────────────────────────────────────────────────────────
function validateStep(
  step: number,
  data: Partial<OnboardingData>,
): Partial<Record<keyof OnboardingData, string>> {
  const errors: Partial<Record<keyof OnboardingData, string>> = {}

  switch (step) {
    case 1:
      // Welkom - geen validatie
      break

    case 2:
      // Privacy - verplicht akkoord vóór persoonsgegevens
      if (!data.privacyAccepted) {
        errors.privacyAccepted = 'U dient akkoord te gaan met de privacyverklaring'
      }
      if (!data.termsAccepted) {
        errors.termsAccepted = 'U dient akkoord te gaan met de voorwaarden'
      }
      break

    case 3:
      // Naam
      if (!data.firstName?.trim()) {
        errors.firstName = 'Voornaam is verplicht'
      } else if (data.firstName.trim().length < 2) {
        errors.firstName = 'Voornaam moet minimaal 2 tekens bevatten'
      }
      break

    case 4:
      // Geboortedatum
      if (!data.dateOfBirth) {
        errors.dateOfBirth = 'Geboortedatum is verplicht'
      } else {
        const dob  = new Date(data.dateOfBirth)
        const now  = new Date()
        const age  = now.getFullYear() - dob.getFullYear()
        if (isNaN(dob.getTime())) {
          errors.dateOfBirth = 'Voer een geldige geboortedatum in'
        } else if (age < 18) {
          errors.dateOfBirth = 'U moet minimaal 18 jaar oud zijn'
        } else if (age > 120) {
          errors.dateOfBirth = 'Controleer uw geboortedatum'
        }
      }
      break

    case 5:
      // Contact
      if (data.email && !isValidEmail(data.email)) {
        errors.email = 'Voer een geldig e-mailadres in'
      }
      break

    case 6:
      // Woonplaats
      if (data.postalCode && !isValidDutchPostalCode(data.postalCode)) {
        errors.postalCode = 'Voer een geldige Nederlandse postcode in (bijv. 1234 AB)'
      }
      break

    case 7:
      // Burgerlijke staat - optioneel
      break

    case 8:
      // Kinderen
      if (data.hasChildren && (data.numberOfChildren ?? 0) < 1) {
        errors.numberOfChildren = 'Voer het aantal kinderen in'
      }
      break

    case 9:
      // In DEMO_MODE: wachtwoord is altijd DEMO_PASSWORD - geen validatie nodig
      if (!DEMO_MODE) {
        if (!data.password) {
          errors.password = 'Wachtwoord is verplicht'
        } else if (data.password.length < 8) {
          errors.password = 'Wachtwoord moet minimaal 8 tekens bevatten'
        }
        if (!data.passwordConfirm) {
          errors.passwordConfirm = 'Bevestig uw wachtwoord'
        } else if (data.password !== data.passwordConfirm) {
          errors.passwordConfirm = 'Wachtwoorden komen niet overeen'
        }
      }
      break
  }

  return errors
}

function canProceed(step: number, data: Partial<OnboardingData>): boolean {
  const errors = validateStep(step, data)
  return Object.keys(errors).length === 0
}

// ── Wizard component ───────────────────────────────────────────────────────────
export function OnboardingWizard() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { toast } = useToast()
  const {
    currentStep,
    totalSteps,
    data,
    isSubmitting,
    nextStep,
    prevStep,
    updateData,
    setSubmitting,
    setComplete,
    setStep,
  } = useOnboardingStore()

  // Demo-modus: prefill naam + geboortedatum + sla stap 1 (welkom) over
  const isDemo = new URLSearchParams(location.search).get('demo') === '1'
  useEffect(() => {
    if (isDemo && currentStep === 1) {
      updateData({
        firstName:   'Jan',
        lastName:    'de Vries',
        dateOfBirth: '1965-04-15',
        email:       'jan.devries@demo.nl',
        phone:       '0612345678',
        privacyAccepted: true,
        termsAccepted:   true,
      })
      setStep(2) // sla welkomstscherm over, ga direct naar privacy/naam
    }
  }, [isDemo])
  const { setAuthenticated } = useAuthStore()

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [touched,     setTouched]     = useState(false)

  const stepErrors = touched ? validateStep(currentStep, data) : {}
  const canGoNext  = canProceed(currentStep, data)

  function handleNext() {
    setTouched(true)
    if (!canGoNext) return
    if (currentStep < totalSteps) {
      setTouched(false)
      nextStep()
    } else {
      handleSubmit()
    }
  }

  function handlePrev() {
    setTouched(false)
    prevStep()
  }

  async function handleSubmit() {
    setSubmitError(null)
    setSubmitting(true)

    try {
      const firstName = data.firstName?.trim() ?? ''
      // In DEMO_MODE: altijd DEMO_PASSWORD gebruiken voor encryptie
      const effectivePassword = DEMO_MODE ? DEMO_PASSWORD : (data.password ?? '')
      const result    = await createProfile(
        firstName,
        {
          lastName:             data.lastName             ?? '',
          dateOfBirth:          data.dateOfBirth          ?? '',
          email:                data.email                ?? '',
          phone:                data.phone                ?? '',
          // V35-P2: separate adresvelden + legacy address-string
          street:               data.street               ?? '',
          houseNumber:          data.houseNumber          ?? '',
          houseNumberAddition:  data.houseNumberAddition  ?? '',
          address:              [
            [data.street, data.houseNumber, data.houseNumberAddition].filter(Boolean).join(' '),
            [data.postalCode, data.city].filter(Boolean).join(' '),
          ].filter(Boolean).join(', '),
        },
        effectivePassword,
      )

      if (!result.succes || !result.data) {
        setSubmitError(result.error?.message ?? 'Er is iets misgegaan. Probeer opnieuw.')
        setSubmitting(false)
        return
      }

      const userId = result.data.userId
      await saveModuleProgress(userId, 'intake', 1, 4)

      setAuthenticated(userId)
      // Onboardinggegevens doorzetten naar wizardStore zodat wizard stap 2+ klopt
      const { setAnswer } = useWizardStore.getState()
      if (typeof data.hasChildren === 'boolean') setAnswer('hasChildren', data.hasChildren)
      // V35-P2: sla burgerlijke staat op als partnerprefill-hint voor wizard stap 1
      // wizardStore.ts (gehashd) niet aangeraakt - aparte localStorage-sleutel
      if (data.maritalStatus) {
        const PARTNER_STATUSES: Record<string, string> = {
          married:    'gehuwd',
          registered: 'geregistreerd',
          cohabiting: 'samenwonend',
        }
        const relationForm = PARTNER_STATUSES[data.maritalStatus] ?? ''
        const hasPartner   = !!relationForm
        localStorage.setItem('wilsapp_onboarding_marital', JSON.stringify({
          maritalStatus: data.maritalStatus,
          hasPartner,
          relationForm,
        }))
      }
      setComplete()
      toast.success(`Welkom, ${firstName}! Uw profiel is aangemaakt.`)
      navigate(HOME_ROUTE, { replace: true })

    } catch (err) {
      console.error('Onboarding submit error:', err)
      setSubmitError('Er is een onverwachte fout opgetreden. Probeer het opnieuw.')
      setSubmitting(false)
    }
  }

  // ── Render huidige stap ────────────────────────────────────────────────────────
  const stepProps = {
    data,
    onChange: (updates: Partial<OnboardingData>) => {
      updateData(updates)
      if (touched) setTouched(true)
    },
    errors: stepErrors,
  }

  const stepContent = (() => {
    switch (currentStep) {
      case 1: return <Step1Welcome />                          // Welkom / uitleg
      case 2: return <Step9Privacy       {...stepProps} />     // Privacy vóór persoonsgegevens
      case 3: return <Step2Name          {...stepProps} />     // Naam
      case 4: return <Step3DateOfBirth   {...stepProps} />     // Geboortedatum
      case 5: return <Step4Contact       {...stepProps} />     // Contactgegevens
      case 6: return <Step5Location      {...stepProps} />     // Woonplaats
      case 7: return <Step6MaritalStatus {...stepProps} />     // Burgerlijke staat
      case 8: return <Step7Children      {...stepProps} />     // Kinderen
      case 9: return <Step8Password      {...stepProps} />     // Wachtwoord
      default: return null
    }
  })()

  return (
    <div className="bg-white rounded-2xl shadow-soft border border-sand-300 p-7">
      {/* Voortgang */}
      <OnboardingProgress
        currentStep={currentStep}
        totalSteps={totalSteps}
        stepLabels={STEP_LABELS}
      />

      {/* Foutmelding bij submit */}
      {submitError && (
        <div className="mb-5">
          <AlertBanner variant="error" message={submitError} dismissible />
        </div>
      )}

      {/* Stapinhoud */}
      <div className="min-h-[280px]">
        {stepContent}
      </div>

      {/* Navigatie */}
      <OnboardingNav
        currentStep={currentStep}
        totalSteps={totalSteps}
        canContinue={canGoNext}
        isSubmitting={isSubmitting}
        onPrev={handlePrev}
        onNext={handleNext}
        nextLabel={currentStep === totalSteps ? 'Account aanmaken' : undefined}
      />
    </div>
  )
}
